/**
 * The activity card — the selected activity's own fields.
 *
 *  1. Nothing is mutated here: every setter is a one-line store call (B9), and no document field
 *     is `v-model`-ed anywhere in this feature.
 *  2. `null` DELETES an optional key (the store's patch convention; `undefined` leaves it alone).
 *     An emptied points field passes `null`, never `0` — `points: 0` is a real authored value.
 *  3. The interaction sub-form is dispatched through `INTERACTION_EDITORS`, the exhaustiveness
 *     guard, never a `v-if` chain on the type.
 *
 * The owning ids arrive as GETTERS: an activity's screen can change under it, and a snapshot would
 * write into the wrong one.
 */
import { computed } from 'vue'

import type { Component, ComputedRef } from 'vue'
import type { NativeSelectOption } from '@shared/ui/fields'
import type { Activity } from './interfaces'
import type { InteractionType } from './types'
import type { ContentBlockOwner } from './store/interfaces'

import { useAssessmentEditorStore } from './store'
import {
  INTERACTION_LABELS,
  INTERACTION_TYPES,
  NO_INTERACTION_LABEL,
} from './interaction-labels'
import { INTERACTION_EDITORS } from '../../ui/assessments/editor/interactions/registry'

/** The empty option's value: `NativeSelect` takes `string | number | null`, and an empty string
 *  reads back unambiguously — the six real values are all non-empty. */
const NO_INTERACTION_VALUE = ''

export interface ActivityEditorProps {
  screenId: string
  activityId: string
}

interface UseActivityEditorOptions {
  screenId: () => string
  activityId: () => string
}

interface UseActivityEditorReturn {
  refActivity: ComputedRef<Activity | null>
  refOwner: ComputedRef<ContentBlockOwner>
  refName: ComputedRef<string>
  refPoints: ComputedRef<string>
  refInteractionType: ComputedRef<InteractionType | ''>
  refInteractionTypeOptions: ComputedRef<NativeSelectOption[]>
  refInteractionEditor: ComputedRef<Component | null>
  setName: (value: string) => void
  setPoints: (value: string | number) => void
  setInteractionType: (value: string) => void
}

export const useActivityEditor = (
  { screenId, activityId }: UseActivityEditorOptions,
): UseActivityEditorReturn => {
  const store = useAssessmentEditorStore()

  const refActivities = computed<Activity[]>(() =>
    store.getScreens.find(screen => screen.id === screenId())?.activities ?? [],
  )

  const refActivity = computed<Activity | null>(() =>
    refActivities.value.find(activity => activity.id === activityId()) ?? null,
  )

  /** `ContentBlockList` is owner-agnostic (T10) — this is the second of its two owners. */
  const refOwner = computed<ContentBlockOwner>(() => ({
    screenId: screenId(),
    activityId: activityId(),
  }))

  const refName = computed<string>(() => refActivity.value?.name ?? '')

  const refPoints = computed<string>(() => {
    const points = refActivity.value?.points
    return points === undefined ? '' : String(points)
  })

  const refInteractionType = computed<InteractionType | ''>(() =>
    refActivity.value?.interaction?.type ?? NO_INTERACTION_VALUE,
  )

  const refInteractionTypeOptions = computed<NativeSelectOption[]>(() => [
    {
      id: NO_INTERACTION_VALUE,
      value: NO_INTERACTION_VALUE,
      text: `${NO_INTERACTION_LABEL} (prompt only)`,
      selected: refInteractionType.value === NO_INTERACTION_VALUE,
    },
    ...INTERACTION_TYPES.map(type => ({
      id: type,
      value: type,
      text: INTERACTION_LABELS[type],
      selected: type === refInteractionType.value,
    })),
  ])

  /** The registry dispatch. `null` for a prompt-only activity — there is nothing to sub-form. */
  const refInteractionEditor = computed<Component | null>(() => {
    const type = refActivity.value?.interaction?.type
    return type ? INTERACTION_EDITORS[type] : null
  })

  /**
   * An emptied field deletes the key (`null`), it does not store `0`. A non-finite number is
   * treated the same rather than writing `NaN`, which would serialise as `null` and fail the
   * schema on a field nobody could see.
   */
  const setPoints = (value: string | number): void => {
    const raw = String(value).trim()
    const parsed = Number(raw)
    store.updateActivity(screenId(), activityId(), {
      points: raw && Number.isFinite(parsed) ? parsed : null,
    })
  }

  /** Emptied ⇒ the key goes, so an unnamed activity stores no `name` at all and the tutor's review
   *  falls back to the prompt. */
  const setName = (value: string): void => {
    store.updateActivity(screenId(), activityId(), { name: value.trim() || null })
  }

  /** `null` clears the interaction, which is legitimate — a prompt-only activity. */
  const setInteractionType = (value: string): void => {
    store.setInteractionType(
      screenId(),
      activityId(),
      value === NO_INTERACTION_VALUE ? null : value as InteractionType,
    )
  }

  return {
    refActivity,
    refOwner,
    refName,
    refPoints,
    refInteractionType,
    refInteractionTypeOptions,
    refInteractionEditor,
    setName,
    setPoints,
    setInteractionType,
  }
}
